import { useState, useEffect } from "react";
import { Calendar, Plus, Check, Loader2 } from "lucide-react";
import { Button } from "../../ui/button";
import axios from "axios";
import { API, getAuthHeader } from "./utils";
import { CompactGameRow, BulkScoreModal, GameModal } from "./schedule";

export const LeagueScheduleSection = ({ league, onRefresh }) => {
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showGameModal, setShowGameModal] = useState(false);
  const [showBulkScoreModal, setShowBulkScoreModal] = useState(false);
  const [editingGame, setEditingGame] = useState(null);
  const [filter, setFilter] = useState("all");

  const teams = league.teams || [];
  const divisions = league.divisions || [];

  const fetchGames = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/leagues/${league.id}/games`, { headers: getAuthHeader() });
      setGames(res.data || []);
    } catch (err) {
      console.error("Failed to fetch games:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGames();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [league.id]);

  const handleSaveGame = async (gameData) => {
    setSaving(true);
    try {
      if (editingGame?.id) {
        await axios.put(`${API}/leagues/${league.id}/games/${editingGame.id}`, gameData, { headers: getAuthHeader() });
      } else {
        await axios.post(`${API}/leagues/${league.id}/games`, gameData, { headers: getAuthHeader() });
      }
      await fetchGames();
      setShowGameModal(false);
      setEditingGame(null);
    } catch (err) {
      console.error("Failed to save game:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteGame = async (gameId) => {
    if (!window.confirm("Delete this game?")) return;
    try {
      await axios.delete(`${API}/leagues/${league.id}/games/${gameId}`, { headers: getAuthHeader() });
      await fetchGames();
    } catch (err) {
      console.error("Failed to delete game:", err);
    }
  };

  const handleUpdateResult = async (game, homeScore, awayScore) => {
    try {
      await axios.put(
        `${API}/leagues/${league.id}/games/${game.id}/result?home_score=${homeScore}&away_score=${awayScore}`, 
        {}, 
        { headers: getAuthHeader() }
      );
      await fetchGames();
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error("Failed to update result:", err);
    }
  };

  const handleBulkScoreUpdate = async (scores) => {
    try {
      for (const { gameId, homeScore, awayScore } of scores) {
        await axios.put(
          `${API}/leagues/${league.id}/games/${gameId}/result?home_score=${homeScore}&away_score=${awayScore}`, 
          {}, 
          { headers: getAuthHeader() }
        );
      }
      await fetchGames();
      if (onRefresh) onRefresh();
      setShowBulkScoreModal(false);
    } catch (err) {
      console.error("Failed to update scores:", err);
    }
  };

  const filteredGames = games.filter(game => {
    if (filter === "upcoming") return !game.is_completed;
    if (filter === "completed") return game.is_completed;
    if (filter === "needs-score") return !game.is_completed && new Date(game.scheduled_date) < new Date();
    return true;
  });

  const gamesByDate = filteredGames.reduce((acc, game) => {
    const date = game.scheduled_date || "Unscheduled";
    if (!acc[date]) acc[date] = [];
    acc[date].push(game);
    return acc;
  }, {});

  const sortedDates = Object.keys(gamesByDate).sort();
  const gamesNeedingScores = games.filter(g => !g.is_completed && new Date(g.scheduled_date) < new Date());

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-4">
          <div>
            <h4 className="text-sm font-bold text-zinc-400 uppercase">Schedule</h4>
            <p className="text-xs text-zinc-500">{games.length} games</p>
          </div>
          <div className="flex bg-zinc-800 rounded p-0.5">
            {[
              { id: "all", label: "All" },
              { id: "upcoming", label: "Upcoming" },
              { id: "completed", label: "Done" },
              { id: "needs-score", label: `Need Score${gamesNeedingScores.length > 0 ? ` (${gamesNeedingScores.length})` : ''}` }
            ].map(f => (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                className={`px-2 py-1 text-xs font-medium rounded ${
                  filter === f.id ? "bg-orange-500 text-white" : "text-zinc-400 hover:text-white"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {gamesNeedingScores.length > 0 && (
            <Button 
              onClick={() => setShowBulkScoreModal(true)}
              variant="outline"
              className="text-xs h-8 border-green-600 text-green-400 hover:bg-green-600/20"
            >
              <Check className="w-3 h-3 mr-1" /> Enter Scores
            </Button>
          )}
          <Button 
            onClick={() => { setEditingGame(null); setShowGameModal(true); }}
            className="bg-orange-500 hover:bg-orange-600 text-xs h-8"
          >
            <Plus className="w-3 h-3 mr-1" /> Add Game
          </Button>
        </div>
      </div>

      {filteredGames.length === 0 ? (
        <div className="text-center py-8 text-zinc-500">
          <Calendar className="w-10 h-10 mx-auto mb-2 opacity-50" />
          <p className="text-sm">{games.length === 0 ? "No games scheduled" : `No ${filter} games`}</p>
        </div>
      ) : (
        <div className="bg-zinc-900/50 rounded-lg border border-zinc-800 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-zinc-500 uppercase border-b border-zinc-800 bg-zinc-900">
                <th className="px-3 py-2 font-medium">Date</th>
                <th className="px-3 py-2 font-medium">Away</th>
                <th className="px-3 py-2 font-medium text-center w-16">Score</th>
                <th className="px-3 py-2 font-medium">Home</th>
                <th className="px-3 py-2 font-medium hidden md:table-cell">Time</th>
                <th className="px-3 py-2 font-medium hidden lg:table-cell">Tags</th>
                <th className="px-3 py-2 font-medium w-20"></th>
              </tr>
            </thead>
            <tbody>
              {sortedDates.map(date => {
                const dateGames = gamesByDate[date];
                return dateGames.map((game, idx) => (
                  <CompactGameRow
                    key={game.id}
                    game={game}
                    showDate={idx === 0}
                    dateLabel={date !== "Unscheduled" 
                      ? new Date(date + "T12:00:00").toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
                      : "TBD"}
                    dateCount={dateGames.length}
                    onEdit={() => { setEditingGame(game); setShowGameModal(true); }}
                    onDelete={() => handleDeleteGame(game.id)}
                    onUpdateResult={handleUpdateResult}
                  />
                ));
              })}
            </tbody>
          </table>
        </div>
      )}

      {showGameModal && (
        <GameModal
          game={editingGame}
          league={league}
          teams={teams}
          divisions={divisions}
          onSave={handleSaveGame}
          onClose={() => { setShowGameModal(false); setEditingGame(null); }}
          saving={saving}
        />
      )}

      {showBulkScoreModal && (
        <BulkScoreModal
          games={gamesNeedingScores}
          onSave={handleBulkScoreUpdate}
          onClose={() => setShowBulkScoreModal(false)}
        />
      )}
    </div>
  );
};
